import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import type { ILandlordOnboardInvite } from 'src/api/onboard-api';
import { useNotifications } from 'src/notifications';
import { queryKeys } from 'src/services/api/constants';
import { apiClient } from 'src/services/api/client';
import { LANDLORD_SELF_INVITE_PATH } from 'src/services/api/onboarding/invites/constants';
import { getCreateInvitesFailureMessage } from 'src/services/api/onboarding/invites/get-create-invites-error';

type ICreateInvitesResponse = {
    invite_codes: ILandlordOnboardInvite[];
    num_invites_sent?: number;
    num_errors?: number;
    error_emails?: string[];
    num_rows_affected?: number;
    num_rows_errors?: number;
    status?: string;
    msg?: string;
};

export type ICreateInvitesError = IStandardErrorResponse & {
    error_emails?: string[];
};

export type ICreateInvitesRequest = {
    emails: string[];
    // is_company_admin: boolean;
}

const invitesQueryKey = [queryKeys.onboardingInvites];

export const useCreateInvites = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<
        ICreateInvitesResponse,
        ICreateInvitesError,
        ICreateInvitesRequest
    >(
        async (payload) => {
            const res = await apiClient(LANDLORD_SELF_INVITE_PATH, {
                method: 'post',
                json: {
                    ...payload,
                    is_company_admin: false,
                },
            }).json<ICreateInvitesResponse>();

            const failureMessage = getCreateInvitesFailureMessage(res);
            if ((res.invite_codes?.length ?? 0) > 0) {
                // Refetch even when some emails failed so successful rows show up.
                queryClient.invalidateQueries(invitesQueryKey);
            }

            if (failureMessage) {
                throw {
                    status: 'error',
                    msg: failureMessage,
                    error_emails: res.error_emails,
                } satisfies ICreateInvitesError;
            }

            return res;
        }, {
            onSuccess: () => {
                addNotification('Invites sent', 'success');
                // Refetch pending invites so the new rows show up in the team list.
                queryClient.invalidateQueries(invitesQueryKey);
            }
        });
};
